// client/src/pages/ProgrammaticAnalytics.js

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import "./Dashboard.css";
import "./ProgrammaticAnalytics.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import Loading from "../components/Loading";
import OmniIntelligencePanel from "../components/OmniIntelligencePanel";
import {
  normalizeAnalyticsEmbedUrl,
  resolveAnalyticsReportSurface,
} from "../components/analyticsReportUtils";
import audioAnalyticsImage from "../assets/audio.jpg";
import ingameAnalyticsImage from "../assets/ingame.jpg";
import ctvAnalyticsImage from "../assets/ctv.jpg";
import videoAnalyticsImage from "../assets/video.jpg";
import mobileAnalyticsImage from "../assets/mobile.jpg";
import displayAnalyticsImage from "../assets/display.jpg";
import doohAnalyticsImage from "../assets/dooh.gif";
import emailAnalyticsImage from "../assets/email.jpg";
import campaignManagementImage from "../assets/campaignmanagement.jpg";
import reverseArrowImage from "../assets/reversearrow.jpg";
import { SOCIAL_PLATFORM_DEFINITIONS } from "./SocialAnalytics";

import { useNavigate } from "react-router-dom";
import usePageAwareness from "../hooks/usePageAwareness";
import useAudienceIntelligenceResolution from "../hooks/useAudienceIntelligenceResolution";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import useResolvedAnalyticsTimeframe from "../hooks/useResolvedAnalyticsTimeframe";
import { getToplinePerformance } from "../services/analyticsToplineService";
import {
  buildAggregateToplineSnapshot,
  hasMeaningfulToplineData,
} from "../utils/analyticsAggregateTopline";
import {
  buildAnalyticsMetricSignature,
  buildAnalyticsScopeKey,
  readCachedAnalyticsIntelligence,
  writeCachedAnalyticsIntelligence,
} from "../utils/analyticsIntelligenceCache";
import {
  buildOmniPlatformIntelligenceModel,
  buildSocialPlatformReadinessEntries,
  resolveIntegrationReadinessBadge,
} from "../utils/omniPlatformIntelligence";
import {
  buildPlatformAnalyticsModel,
  formatFreshness,
} from "../platformAnalytics/platformAnalyticsReportModel";
import {
  OMNI_PERFORMANCE_METRICS,
  buildOmniPerformanceMetricCards,
  buildOmniPerformanceModel,
  describeOmniContributionTotal,
  formatOmniPerformanceValue,
  resolveOmniPerformanceMetric,
} from "../platformAnalytics/omniPerformanceModel";
import {
  describePartialChannelReporting,
  listPartiallyReportingChannels,
  readChannelReporting,
  resolveChannelReportingReady,
} from "../utils/omniChannelReporting";
import { readToplineReportingWindow } from "../platformAnalytics/toplineReportingWindow";
import { describeReportingScopeCoverage } from "../platformAnalytics/reportingScopeCoverage";
import { resolvePlatformBranding } from "../utils/platformBranding";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const safeBase = (base) => String(base || "").replace(/\/+$/, "");

const INITIAL_LOADER_MAX_HOLD_MS = 1800;
const STACKADAPT_PROGRAMMATIC_CHANNELS = Object.freeze([
  "DOOH",
  "CTV",
  "Video",
  "Audio",
  "Display",
  "Mobile",
  "In-Game",
  "Email",
]);
const PROGRAMMATIC_PAGE_TITLE = "Omni Analytics";
const CLIENT_PROGRAMMATIC_PROVIDER_LABEL = "Omni Analytics";
const PROGRAMMATIC_ROLLUP_INTELLIGENCE_CACHE_KEY =
  "analytics:intelligence:programmatic-rollup:v2";
const OMNI_PERFORMANCE_PANEL_TITLE = "Omni Performance";
const OMNI_PERFORMANCE_PANEL_SUBTITLE =
  "Unified governed performance across the Cphere platform estate.";
const OMNI_PERFORMANCE_PRIMARY_ACTION_LABEL = "OPEN PLATFORM ANALYTICS";
const OMNI_PERFORMANCE_PRIMARY_ACTION_ROUTE = "/platform-analytics";
// Canonical daily-series posture shared with Platform Analytics: the governed
// daily reporting contract is not certified yet, and nothing is interpolated.
const OMNI_PERFORMANCE_DAILY_SERIES_NOTE =
  "A daily delivery series appears here once the daily reporting contract is certified — nothing is interpolated in the meantime.";
const CLIENT_PROGRAMMATIC_STATUS_FALLBACK =
  "Unable to load Cphere programmatic status.";
const PROGRAMMATIC_METRIC_SKELETON_LABELS = Object.freeze([
  "Spend",
  "Impressions",
  "Clicks",
  "CTR",
  "Top Performing Platform",
]);
const PROGRAMMATIC_CHANNEL_FIELD_CANDIDATES = Object.freeze({
  dooh: ["doohLink", "dooh", "doohURL", "dooh_report", "dooh_analytics"],
  ctv: ["ctvLink", "ctv", "ctvURL", "ctv_report", "ctv_analytics"],
  video: ["videoLink", "video", "videoURL", "video_report", "video_analytics"],
  audio: ["audioLink", "audio", "audioURL", "audio_report", "audio_analytics"],
  display: ["displayLink", "display", "displayURL", "display_report", "display_analytics"],
  mobile: ["mobileLink", "mobile", "mobileURL", "mobile_report", "mobile_analytics"],
  ingame: ["ingameLink", "ingame", "in_game", "ingameURL", "ingame_report", "ingame_analytics"],
  email: ["emailLink", "email", "emailURL", "email_report", "email_analytics"],
});
const PROGRAMMATIC_CHANNEL_PRIORITY = Object.freeze([
  "dooh",
  "ctv",
  "video",
  "audio",
  "display",
  "mobile",
  "ingame",
  "email",
]);
const PROGRAMMATIC_CHANNEL_HERO_BY_LABEL = Object.freeze({
  DOOH: doohAnalyticsImage,
  CTV: ctvAnalyticsImage,
  Video: videoAnalyticsImage,
  Audio: audioAnalyticsImage,
  Display: displayAnalyticsImage,
  Mobile: mobileAnalyticsImage,
  "In-Game": ingameAnalyticsImage,
  Email: emailAnalyticsImage,
});
const PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY = Object.freeze({
  dooh: "/dooh-analytics",
  ctv: "/ctv-analytics",
  video: "/video-analytics",
  audio: "/audio-analytics",
  display: "/display-analytics",
  mobile: "/mobile-analytics",
  ingame: "/ingame-analytics",
  email: "/email-analytics",
});
export const OMNI_CHANNEL_DEFINITIONS = Object.freeze([
  { key: "dooh", label: "DOOH", route: "/dooh-analytics", image: doohAnalyticsImage },
  { key: "ctv", label: "CTV", route: "/ctv-analytics", image: ctvAnalyticsImage },
  { key: "video", label: "Video", route: "/video-analytics", image: videoAnalyticsImage },
  { key: "audio", label: "Audio", route: "/audio-analytics", image: audioAnalyticsImage },
  { key: "display", label: "Display", route: "/display-analytics", image: displayAnalyticsImage },
  { key: "mobile", label: "Mobile", route: "/mobile-analytics", image: mobileAnalyticsImage },
  { key: "ingame", label: "In-Game", route: "/ingame-analytics", image: ingameAnalyticsImage },
  { key: "email", label: "Email", route: "/email-analytics", image: emailAnalyticsImage },
]);
export const OMNI_ANALYTICS_TILE_DEFINITIONS = Object.freeze(
  [...SOCIAL_PLATFORM_DEFINITIONS, ...OMNI_CHANNEL_DEFINITIONS].filter(
    (tile, index, tiles) =>
      tiles.findIndex(
        (candidate) => candidate.key === tile.key || candidate.route === tile.route
      ) === index
  )
);

function isUsableStackAdaptStatusPayload(payload) {
  return Boolean(
    payload &&
      typeof payload === "object" &&
      String(payload.provider || "").toLowerCase() === "stackadapt" &&
      typeof payload.liveReadState === "string"
  );
}

function normalizeClientProgrammaticStatusMessage(value) {
  const text = String(value || "").trim();
  if (!text) return CLIENT_PROGRAMMATIC_STATUS_FALLBACK;

  const lower = text.toLowerCase();
  if (lower.includes("stackadapt")) {
    return CLIENT_PROGRAMMATIC_STATUS_FALLBACK;
  }

  return text.replace(/stackadapt/gi, "Cphere");
}

function getFirstNonEmptyAdvertiserField(source, fields = []) {
  if (!source || typeof source !== "object") return "";
  for (const field of fields) {
    const value = String(source?.[field] || "").trim();
    if (value) return value;
  }
  return "";
}

function normalizeProgrammaticChannelLabel(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const lower = text.toLowerCase();
  if (lower === "ctv") return "CTV";
  if (lower === "dooh") return "DOOH";
  if (lower === "ingame" || lower === "in-game" || lower === "in_game") return "In-Game";
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolveProgrammaticChannelImage(label) {
  const normalized = normalizeProgrammaticChannelLabel(label);
  return PROGRAMMATIC_CHANNEL_HERO_BY_LABEL[normalized] || null;
}

function normalizeProgrammaticChannelKey(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  if (!normalized) return "";
  if (normalized === "ctv") return "ctv";
  if (normalized === "dooh") return "dooh";
  if (normalized === "video") return "video";
  if (normalized === "audio") return "audio";
  if (normalized === "display") return "display";
  if (normalized === "mobile") return "mobile";
  if (normalized === "ingame") return "ingame";
  if (normalized === "email") return "email";
  return "";
}

function getProgrammaticChannelDisplayLabel(key) {
  if (key === "ingame") return "In-Game";
  if (key === "ctv") return "CTV";
  if (key === "dooh") return "DOOH";
  return key ? key.toUpperCase() : "";
}

function getPrioritizedProgrammaticConnectedChannels(surface, fallbackSurface) {
  const rawCandidates = [
    ...(Array.isArray(surface?.connectedChannels) ? surface.connectedChannels : []),
    ...(Array.isArray(surface?.connectedChannelLabels) ? surface.connectedChannelLabels : []),
    surface?.primaryChannel,
    surface?.primaryChannelLabel,
    ...(Array.isArray(fallbackSurface?.connectedChannels) ? fallbackSurface.connectedChannels : []),
    ...(Array.isArray(fallbackSurface?.connectedChannelLabels) ? fallbackSurface.connectedChannelLabels : []),
    fallbackSurface?.primaryChannel,
    fallbackSurface?.primaryChannelLabel,
  ];

  const normalizedSet = new Set(
    rawCandidates
      .map((value) => normalizeProgrammaticChannelKey(value))
      .filter(Boolean)
  );

  return PROGRAMMATIC_CHANNEL_PRIORITY.filter((key) => normalizedSet.has(key));
}

function resolvePrioritizedProgrammaticReportSurface(surface, rawAdvertiser) {
  const fallbackSurface = buildAdvertiserProgrammaticFallback(rawAdvertiser).reportSurface;
  const baseSurface = surface || fallbackSurface;
  const connectedChannels = getPrioritizedProgrammaticConnectedChannels(surface, fallbackSurface);
  const connectedChannelLabels = connectedChannels.map((key) =>
    getProgrammaticChannelDisplayLabel(key)
  );
  const prioritizedChannelWithUrl = connectedChannels.find((key) =>
    Boolean(
      getFirstNonEmptyAdvertiserField(
        rawAdvertiser,
        PROGRAMMATIC_CHANNEL_FIELD_CANDIDATES[key] || []
      )
    )
  );

  if (prioritizedChannelWithUrl) {
    const prioritizedUrl = getFirstNonEmptyAdvertiserField(
      rawAdvertiser,
      PROGRAMMATIC_CHANNEL_FIELD_CANDIDATES[prioritizedChannelWithUrl] || []
    );

    return resolveAnalyticsReportSurface({
      ...(baseSurface || {}),
      connectedChannels,
      connectedChannelLabels,
      primaryChannel: prioritizedChannelWithUrl,
      primaryChannelLabel: getProgrammaticChannelDisplayLabel(prioritizedChannelWithUrl),
      reportUrl: prioritizedUrl,
      openUrl: prioritizedUrl,
      embedUrl: normalizeAnalyticsEmbedUrl(prioritizedUrl).embedUrl,
      resolution: "advertiser_programmatic_channel_report",
    });
  }

  const resolvedPrimaryChannel =
    normalizeProgrammaticChannelKey(baseSurface?.primaryChannel) ||
    normalizeProgrammaticChannelKey(baseSurface?.primaryChannelLabel) ||
    fallbackSurface?.primaryChannel ||
    null;

  return resolveAnalyticsReportSurface({
    ...(baseSurface || {}),
    connectedChannels:
      connectedChannels.length ? connectedChannels : fallbackSurface.connectedChannels || [],
    connectedChannelLabels:
      connectedChannelLabels.length
        ? connectedChannelLabels
        : fallbackSurface.connectedChannelLabels || [],
    primaryChannel: resolvedPrimaryChannel,
    primaryChannelLabel:
      resolvedPrimaryChannel
        ? getProgrammaticChannelDisplayLabel(resolvedPrimaryChannel)
        : baseSurface?.primaryChannelLabel || fallbackSurface?.primaryChannelLabel || null,
  });
}

function buildAdvertiserProgrammaticFallback(rawAdvertiser) {
  const connectedChannels = PROGRAMMATIC_CHANNEL_PRIORITY.filter((key) =>
    Boolean(
      getFirstNonEmptyAdvertiserField(
        rawAdvertiser,
        PROGRAMMATIC_CHANNEL_FIELD_CANDIDATES[key] || []
      )
    )
  );

  const primaryChannel = connectedChannels[0] || null;
  const primaryUrl = primaryChannel
    ? getFirstNonEmptyAdvertiserField(
        rawAdvertiser,
        PROGRAMMATIC_CHANNEL_FIELD_CANDIDATES[primaryChannel] || []
      )
    : "";

  return {
    connectedChannels,
    connectedChannelLabels: connectedChannels.map((key) =>
      STACKADAPT_PROGRAMMATIC_CHANNELS.find(
        (label) => label.toLowerCase().replace(/[^a-z]/g, "") === key.replace(/[^a-z]/g, "")
      ) || (key === "ingame" ? "In-Game" : key.toUpperCase())
    ),
    reportSurface: {
      provider: "stackadapt",
      providerLabel: CLIENT_PROGRAMMATIC_PROVIDER_LABEL,
      channelFamily: "programmatic",
      connectedChannels,
      connectedChannelLabels: connectedChannels.map((key) =>
        STACKADAPT_PROGRAMMATIC_CHANNELS.find(
          (label) => label.toLowerCase().replace(/[^a-z]/g, "") === key.replace(/[^a-z]/g, "")
        ) || (key === "ingame" ? "In-Game" : key.toUpperCase())
      ),
      available: Boolean(primaryUrl),
      resolution: primaryUrl
        ? "advertiser_programmatic_channel_report"
        : "missing_report_link",
      primaryChannel,
      primaryChannelLabel:
        primaryChannel === "ingame"
          ? "In-Game"
          : primaryChannel
          ? primaryChannel.toUpperCase()
          : null,
      embedUrl: primaryUrl ? normalizeAnalyticsEmbedUrl(primaryUrl).embedUrl : "",
      openUrl: primaryUrl || "",
    },
  };
}

function buildClientProgrammaticUnavailableState({
  advertiserId,
  rawAdvertiser,
}) {
  const fallback = buildAdvertiserProgrammaticFallback(rawAdvertiser);
  return {
    advertiserId: Number(advertiserId) || null,
    provider: "stackadapt",
    providerLabel: CLIENT_PROGRAMMATIC_PROVIDER_LABEL,
    connected: false,
    advertiserMapped: false,
    liveReadState: "status_unavailable",
    statusOnly: true,
    connectedChannels: fallback.connectedChannels,
    connectedChannelLabels: fallback.connectedChannelLabels,
    reportSurface: fallback.reportSurface,
    integrationNotes: {
      provider: "stackadapt",
      source: "frontend_status_fallback",
      reportSurface: fallback.reportSurface,
      connectedChannelLabels: fallback.connectedChannelLabels,
    },
    // Report linkage no longer drives anything this panel offers, so the
    // fallback speaks to provider readiness alone.
    message:
      "Cphere programmatic status is currently unavailable for this advertiser. Provider readiness may still be settling.",
  };
}

function mergeStackAdaptHealthWithFallback(payload, fallbackState) {
  if (!isUsableStackAdaptStatusPayload(payload)) {
    return fallbackState || null;
  }

  if (!fallbackState) {
    return payload;
  }

  return {
    ...fallbackState,
    ...payload,
    connectedChannelLabels:
      Array.isArray(payload.connectedChannelLabels) &&
      payload.connectedChannelLabels.length
        ? payload.connectedChannelLabels
        : fallbackState.connectedChannelLabels,
    connectedChannels:
      Array.isArray(payload.connectedChannels) && payload.connectedChannels.length
        ? payload.connectedChannels
        : fallbackState.connectedChannels,
    reportSurface: payload.reportSurface || fallbackState.reportSurface,
    integrationNotes: {
      ...(fallbackState.integrationNotes || {}),
      ...(payload.integrationNotes || {}),
      reportSurface:
        payload?.integrationNotes?.reportSurface ||
        payload.reportSurface ||
        fallbackState.reportSurface,
      connectedChannelLabels:
        payload?.integrationNotes?.connectedChannelLabels ||
        payload.connectedChannelLabels ||
        fallbackState.connectedChannelLabels,
    },
  };
}

function renderProgrammaticMetricSkeletonCards() {
  return PROGRAMMATIC_METRIC_SKELETON_LABELS.map((label) => (
    <div
      key={`programmatic-skeleton-${label}`}
      className="omni-analytics-provider-metric-card omni-analytics-provider-metric-card-skeleton"
      aria-hidden="true"
    >
      <div className="omni-analytics-provider-metric-label">{label}</div>
      <div className="omni-analytics-provider-skeleton-line omni-analytics-provider-skeleton-line-value" />
      <div className="omni-analytics-provider-skeleton-line omni-analytics-provider-skeleton-line-meta" />
    </div>
  ));
}

/*
 * The KPI strip waits on the canonical estate now, not on the per-channel
 * topline sweep it no longer displays. `programmaticToplinesReady` survives as
 * the parameter name's default so the governed readiness question — "has the
 * source of the figures answered yet?" — keeps one call site and one meaning.
 */
export function resolveProgrammaticMetricLoadingState({
  currentAdvertiserRaw,
  programmaticToplinesReady,
  canonicalEstateReady = programmaticToplinesReady,
  prioritizedMetricCards = [],
}) {
  return Boolean(
    currentAdvertiserRaw &&
      !canonicalEstateReady &&
      (!Array.isArray(prioritizedMetricCards) || prioritizedMetricCards.length === 0)
  );
}

const ProgrammaticAnalytics = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PROGRAMMATIC_PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  /*
   * The per-channel programmatic topline sweep still runs: it carries the
   * pacing evidence (pre-flight, meaningful-delivery) the canonical estate
   * cannot express, and it keeps the intelligence cache warm. What it no
   * longer does is state the panel's figures — those come from the canonical
   * estate only — so its metric/refresh slots are write-only now.
   */
  const [, setAggregateToplineMetrics] = useState([]);
  const [, setAggregateToplineMetadata] = useState({});
  const [programmaticToplines, setProgrammaticToplines] = useState({});
  const [, setProgrammaticToplinesReady] = useState(false);
  const [, setProgrammaticToplinesRefreshing] = useState(false);
  const [, setProgrammaticToplinesRefreshError] = useState("");
  const [metaProviderState, setMetaProviderState] = useState(null);
  const [availableSocialPlatformKeys, setAvailableSocialPlatformKeys] = useState([]);
  const [integrationReadinessByPlatform, setIntegrationReadinessByPlatform] = useState({});
  const [omniReadinessLoading, setOmniReadinessLoading] = useState(false);
  const [omniReadinessError, setOmniReadinessError] = useState("");
  const [stackAdaptHealth, setStackAdaptHealth] = useState(null);
  const [, setStackAdaptHealthReady] = useState(false);
  const [, setStackAdaptHealthError] = useState("");
  const [, setStackAdaptCampaigns] = useState([]);
  const [, setStackAdaptCampaignsReady] = useState(false);
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const [loaderSafetyElapsed, setLoaderSafetyElapsed] = useState(false);
  const [estateModel, setEstateModel] = useState(null);
  const [estateModelReady, setEstateModelReady] = useState(false);
  const [estateModelError, setEstateModelError] = useState("");
  const [contributionMetricKey, setContributionMetricKey] = useState("spend");
  const initialLoaderDismissedRef = useRef(false);

  const clearAdvertiserScopedPresentationState = useCallback(() => {
    setAggregateToplineMetrics([]);
    setAggregateToplineMetadata({});
    setProgrammaticToplines({});
    setProgrammaticToplinesReady(false);
    setProgrammaticToplinesRefreshing(false);
    setProgrammaticToplinesRefreshError("");
    setMetaProviderState(null);
    setAvailableSocialPlatformKeys([]);
    setIntegrationReadinessByPlatform({});
    setOmniReadinessLoading(true);
    setOmniReadinessError("");
    setStackAdaptHealth(null);
    setStackAdaptHealthReady(false);
    setStackAdaptHealthError("");
    setStackAdaptCampaigns([]);
    setStackAdaptCampaignsReady(false);
    setEstateModel(null);
    setEstateModelReady(false);
    setEstateModelError("");
    setContributionMetricKey("spend");
  }, []);

  const {
    selectedAdvertiserId,
    visibleAdvertisers,
    currentAdvertiser: currentAdvertiserRecord,
    catalogReady,
    accessReady,
    loading: advertiserCatalogLoading,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: handleAdvertiserSelectorOpen,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedPresentationState,
  });

  // ---------- helpers ----------
  const getToken = () =>
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    "";

  const hasChannelLink = (advRaw, channelKey, explicitKeys = []) => {
    if (!advRaw) return false;

    for (const key of explicitKeys) {
      const val = advRaw[key];
      if (typeof val === "string" && val.trim() !== "") return true;
    }

    if (!channelKey) return false;
    const channel = channelKey.toLowerCase();

    return Object.entries(advRaw).some(([key, value]) => {
      if (typeof value !== "string") return false;
      const k = key.toLowerCase();

      const matchesChannel = k.includes(channel);
      const matchesType = ["link", "url", "report", "looker", "analytics"].some(
        (frag) => k.includes(frag)
      );

      return matchesChannel && matchesType && value.trim() !== "";
    });
  };

  // ---------- current advertiser record + connected flags ----------
  const currentAdvertiserRaw = currentAdvertiserRecord?.raw ?? null;
  const governedAdvertiserId = currentAdvertiserRecord?.id ?? null;
  const governedAdvertiserName = currentAdvertiserRecord?.name || "";

  const audioConnected = hasChannelLink(currentAdvertiserRaw, "audio", [
    "audioLink",
    "audio",
    "audioURL",
    "audio_report",
    "audio_analytics",
  ]);

  const ingameConnected = hasChannelLink(currentAdvertiserRaw, "ingame", [
    "ingameLink",
    "ingame",
    "in_game",
    "ingameURL",
    "ingame_report",
    "ingame_analytics",
  ]);

  const ctvConnected = hasChannelLink(currentAdvertiserRaw, "ctv", [
    "ctvLink",
    "ctv",
    "ctvURL",
    "ctv_report",
    "ctv_analytics",
  ]);

  const videoConnected = hasChannelLink(currentAdvertiserRaw, "video", [
    "videoLink",
    "video",
    "videoURL",
    "video_report",
    "video_analytics",
  ]);

  const mobileConnected = hasChannelLink(currentAdvertiserRaw, "mobile", [
    "mobileLink",
    "mobile",
    "mobileURL",
    "mobile_report",
    "mobile_analytics",
  ]);

  const displayConnected = hasChannelLink(currentAdvertiserRaw, "display", [
    "displayLink",
    "display",
    "displayURL",
    "display_report",
    "display_analytics",
  ]);

  const doohConnected = hasChannelLink(currentAdvertiserRaw, "dooh", [
    "doohLink",
    "dooh",
    "doohURL",
    "dooh_report",
    "dooh_analytics",
  ]);

  const emailConnected = hasChannelLink(currentAdvertiserRaw, "email", [
    "emailLink",
    "email",
    "emailURL",
    "email_report",
    "email_analytics",
  ]);
  const metaReportingReady = useMemo(
    () => Boolean(metaProviderState?.reportingAvailable || metaProviderState?.connected),
    [metaProviderState]
  );
  const socialPlatformEntries = useMemo(
    () =>
      buildSocialPlatformReadinessEntries({
        definitions: SOCIAL_PLATFORM_DEFINITIONS,
        rawAdvertiser: currentAdvertiserRaw,
        availablePlatformKeys: availableSocialPlatformKeys,
        integrationReadinessByPlatform,
        metaReportingReady,
      }),
    [
      availableSocialPlatformKeys,
      currentAdvertiserRaw,
      integrationReadinessByPlatform,
      metaReportingReady,
    ]
  );
  const stackAdaptReportingReady =
    stackAdaptHealth?.liveReadState === "advertiser_scoped_ready";
  const omniChannelEntries = useMemo(
    () =>
      OMNI_CHANNEL_DEFINITIONS.map((channel) => {
        const connected = {
          dooh: doohConnected,
          ctv: ctvConnected,
          video: videoConnected,
          audio: audioConnected,
          display: displayConnected,
          mobile: mobileConnected,
          ingame: ingameConnected,
          email: emailConnected,
        }[channel.key];
        return {
          ...channel,
          connected,
          hasLinkSignal: connected,
          // Ready means the channel's canonical reporting is complete for every
          // provider known to deliver it — not that one provider is healthy.
          reportingReady: Boolean(connected && resolveChannelReportingReady(estateModel?.channelReporting, channel.key, stackAdaptReportingReady)),
          statusBadgeLabel: connected
            ? resolveChannelReportingReady(estateModel?.channelReporting, channel.key, stackAdaptReportingReady)
              ? "REPORTING READY"
              : "REPORT LINK"
            : "",
        };
      }),
    [
      audioConnected,
      ctvConnected,
      displayConnected,
      doohConnected,
      emailConnected,
      ingameConnected,
      mobileConnected,
      estateModel,
      stackAdaptReportingReady,
      videoConnected,
    ]
  );

  const { timeframe, advertiserFlightRange, reportingScope } =
    useResolvedAnalyticsTimeframe(governedAdvertiserId, currentAdvertiserRaw);
  /*
   * What ALL CAMPAIGNS covers, projected from the one governed scope contract.
   * Never counted here: the panel states the server's coverage or states
   * nothing.
   */
  const estateCoverage = useMemo(
    () => describeReportingScopeCoverage(reportingScope),
    [reportingScope]
  );

  const sharedAudienceResolution = useAudienceIntelligenceResolution({
    advertiserId: governedAdvertiserId,
    platform: "omni",
    startDate: timeframe?.start || null,
    endDate: timeframe?.end || null,
  });

  useEffect(() => {
    if (!governedAdvertiserId || !API_BASE_URL) {
      setMetaProviderState(null);
      setAvailableSocialPlatformKeys([]);
      setIntegrationReadinessByPlatform({});
      setOmniReadinessLoading(false);
      setOmniReadinessError("");
      return undefined;
    }

    const token = getToken();
    if (!token) {
      setMetaProviderState(null);
      setAvailableSocialPlatformKeys([]);
      setIntegrationReadinessByPlatform({});
      setOmniReadinessLoading(false);
      setOmniReadinessError("");
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setOmniReadinessLoading(true);
    setOmniReadinessError("");

    const headers = { Authorization: `Bearer ${token}` };
    const platformParams = new URLSearchParams({
      advertiserId: String(governedAdvertiserId),
      datePreset: String(timeframe?.datePreset || "last_30d"),
    });
    if (timeframe?.start) platformParams.set("startDate", String(timeframe.start));
    if (timeframe?.end) platformParams.set("endDate", String(timeframe.end));

    Promise.allSettled([
      fetch(`${safeBase(API_BASE_URL)}/api/optimus/platforms?${platformParams.toString()}`, {
        headers,
        cache: "no-store",
        signal: controller.signal,
      }).then(async (response) => {
        if (!response.ok) throw new Error("Platform reporting readiness unavailable");
        return response.json().catch(() => ({}));
      }),
      fetch(
        `${safeBase(API_BASE_URL)}/api/integrations/${encodeURIComponent(
          governedAdvertiserId
        )}?ts=${Date.now()}`,
        {
          headers,
          cache: "no-store",
          signal: controller.signal,
        }
      ).then(async (response) => {
        if (!response.ok) throw new Error("Integration readiness unavailable");
        return response.json().catch(() => ({}));
      }),
    ]).then(([platformResult, integrationResult]) => {
      if (cancelled) return;

      if (platformResult.status === "fulfilled") {
        const payload = platformResult.value || {};
        setMetaProviderState(payload?.providerStates?.meta || null);
        setAvailableSocialPlatformKeys(
          Array.isArray(payload?.tabs)
            ? payload.tabs
                .map((tab) => String(tab?.id || "").trim().toLowerCase())
                .filter((id) => id && id !== "all")
            : []
        );
      } else {
        setMetaProviderState(null);
        setAvailableSocialPlatformKeys([]);
      }

      if (integrationResult.status === "fulfilled") {
        const payload = integrationResult.value || {};
        setIntegrationReadinessByPlatform({
          shopify: resolveIntegrationReadinessBadge(payload?.SHOPIFY),
          linkedin: resolveIntegrationReadinessBadge(payload?.LINKEDIN),
        });
      } else {
        setIntegrationReadinessByPlatform({});
      }

      if (
        platformResult.status === "rejected" ||
        integrationResult.status === "rejected"
      ) {
        setOmniReadinessError(
          "Some readiness sources are unavailable; unaffected platform evidence remains visible."
        );
      }
      setOmniReadinessLoading(false);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    governedAdvertiserId,
    timeframe?.datePreset,
    timeframe?.end,
    timeframe?.start,
  ]);

  // Canonical estate performance: the same governed advertiser-scoped
  // reporting endpoint and projection that power Platform Analytics
  // (client-billed basis; measurement-only channels excluded server-model
  // side). No second cross-platform arithmetic engine exists here.
  useEffect(() => {
    if (!governedAdvertiserId || !API_BASE_URL) {
      setEstateModel(null);
      setEstateModelReady(false);
      setEstateModelError("");
      return undefined;
    }

    const token = getToken();
    if (!token) {
      setEstateModel(null);
      setEstateModelReady(true);
      setEstateModelError("");
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setEstateModelReady(false);
    setEstateModelError("");

    const params = new URLSearchParams({
      advertiserId: String(governedAdvertiserId),
    });
    if (timeframe?.start) params.set("startDate", String(timeframe.start));
    if (timeframe?.end) params.set("endDate", String(timeframe.end));
    /*
     * No viewer zone is sent. The reporting cutoff is the backend's reporting
     * calendar day (Eastern Time) for every surface and every viewer, so this
     * panel and Cortex home can never disagree by a day's delivery.
     */

    fetch(
      `${safeBase(API_BASE_URL)}/api/dashboard/performance-summary?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
        signal: controller.signal,
      }
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`estate performance summary unavailable (${response.status})`);
        }
        return response.json().catch(() => null);
      })
      .then((payload) => {
        if (cancelled) return;
        setEstateModel(payload ? {
          ...buildPlatformAnalyticsModel(payload),
          channelReporting: readChannelReporting(payload),
          reportingWindow: readToplineReportingWindow({ raw: { effectiveReportingWindow:
            (payload.canonicalReporting || payload.canonicalSummary || payload).timeframe,
          } }),
        } : null);
        setEstateModelReady(true);
      })
      .catch((error) => {
        if (cancelled || controller.signal.aborted) return;
        console.error(
          "[ProgrammaticAnalytics][OmniPerformance] canonical estate fetch failed",
          error
        );
        setEstateModel(null);
        setEstateModelReady(true);
        setEstateModelError(
          "Governed estate performance is currently unavailable for this window."
        );
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [governedAdvertiserId, timeframe?.start, timeframe?.end]);

  const livePlatforms = useMemo(
    () =>
      [
        doohConnected ? { key: "dooh", label: "DOOH", route: PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY.dooh } : null,
        ctvConnected ? { key: "ctv", label: "CTV", route: PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY.ctv } : null,
        videoConnected ? { key: "video", label: "Video", route: PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY.video } : null,
        audioConnected ? { key: "audio", label: "Audio", route: PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY.audio } : null,
        displayConnected ? { key: "display", label: "Display", route: PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY.display } : null,
        mobileConnected ? { key: "mobile", label: "Mobile", route: PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY.mobile } : null,
        ingameConnected ? { key: "ingame", label: "In-Game", route: PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY.ingame } : null,
        emailConnected ? { key: "email", label: "Email", route: PROGRAMMATIC_CHANNEL_ROUTE_BY_KEY.email } : null,
      ].filter(Boolean),
    [
      audioConnected,
      ctvConnected,
      displayConnected,
      doohConnected,
      emailConnected,
      ingameConnected,
      mobileConnected,
      videoConnected,
    ]
  );

  const programmaticAggregateScopeKey = useMemo(
    () =>
      governedAdvertiserId
        ? buildAnalyticsScopeKey({
            page: "programmatic-rollup",
            advertiserId: governedAdvertiserId,
            timeframe,
            advertiserFlightRange,
            platforms: livePlatforms.map((platform) => platform.key).sort(),
          })
        : "",
    [advertiserFlightRange, governedAdvertiserId, livePlatforms, timeframe]
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      if (!governedAdvertiserId || !livePlatforms.length) {
        if (!cancelled) {
          setAggregateToplineMetrics([]);
          setAggregateToplineMetadata({});
          setProgrammaticToplines({});
          setProgrammaticToplinesReady(true);
          setProgrammaticToplinesRefreshing(false);
          setProgrammaticToplinesRefreshError("");
        }
        return;
      }

      const cached = readCachedAnalyticsIntelligence(PROGRAMMATIC_ROLLUP_INTELLIGENCE_CACHE_KEY, {
        scopeKey: programmaticAggregateScopeKey,
      });
      const cachedPayload = cached?.payload || null;

      if (!cancelled) {
        if (cachedPayload) {
          setAggregateToplineMetrics(cachedPayload.metrics || []);
          setAggregateToplineMetadata(cachedPayload.metadata || {});
          setProgrammaticToplines(cachedPayload.toplinesByPlatform || {});
          setProgrammaticToplinesReady(true);
        } else {
          setAggregateToplineMetrics([]);
          setAggregateToplineMetadata({});
          setProgrammaticToplines({});
          setProgrammaticToplinesReady(false);
        }
        setProgrammaticToplinesRefreshing(true);
        setProgrammaticToplinesRefreshError("");
      }

      /*
       * The rollup-wide seed request is gone with the KPI strip it served: it
       * priced an aggregate through the programmatic rate card, painted it as
       * the panel's headline, and blocked the per-channel sweep behind one
       * more round trip. The panel's figures come from the canonical estate
       * now, so the sweep starts immediately and carries pacing evidence only.
       */
      const platformResults = await Promise.all(
        livePlatforms.map(async (platform) => {
          try {
            const topline = await getToplinePerformance({
              advertiserId: governedAdvertiserId,
              timeframe,
              advertiserFlightRange,
              platformKey: platform.key,
              signal: controller.signal,
              applyProgrammaticCampaignPricing: true,
            });
            return [platform.key, topline];
          } catch (error) {
            if (cancelled || controller.signal.aborted) {
              return [platform.key, null];
            }
            console.error("[ProgrammaticAnalytics][Topline] platform fetch failed", platform.key, error);
            return [platform.key, null];
          }
        })
      );

      if (cancelled) return;

      const toplinesByPlatform = Object.fromEntries(platformResults);
      setProgrammaticToplines(toplinesByPlatform);

      const snapshot = buildAggregateToplineSnapshot({
        pageLabel: PROGRAMMATIC_PAGE_TITLE,
        platforms: livePlatforms,
        toplines: toplinesByPlatform,
      });

      const nextMetrics = snapshot.metrics || [];
      setAggregateToplineMetrics(nextMetrics);
      setAggregateToplineMetadata(snapshot.metadata || {});
      if (!nextMetrics.length) {
        setProgrammaticToplinesRefreshError("No common reporting window with usable measurements is available.");
      }
      setProgrammaticToplinesReady(true);
      setProgrammaticToplinesRefreshing(false);
      if (nextMetrics.length) setProgrammaticToplinesRefreshError("");
      writeCachedAnalyticsIntelligence(PROGRAMMATIC_ROLLUP_INTELLIGENCE_CACHE_KEY, {
        scopeKey: programmaticAggregateScopeKey,
        metricSignature: buildAnalyticsMetricSignature({
          metrics: nextMetrics,
          metadata: snapshot.metadata || {},
          sources: Object.entries(toplinesByPlatform).map(([key, topline]) => ({
            key,
            metricSignature: topline?.raw?.metricSignature || "",
            sourceFreshness:
              topline?.raw?.sourceUpdatedAt ||
              topline?.raw?.latestIngestedAt ||
              topline?.raw?.snapshotUpdatedAt ||
              "",
          })),
        }),
        payload: {
          metrics: nextMetrics,
          metadata: snapshot.metadata || {},
          toplinesByPlatform,
        },
      });
    };

    run().catch((error) => {
      if (!cancelled && !controller.signal.aborted) {
        console.error("[ProgrammaticAnalytics][Topline] refresh failed", error);
        setProgrammaticToplinesRefreshing(false);
        setProgrammaticToplinesRefreshError("Live governed reporting refresh is delayed. Showing the latest available KPI values.");
        setProgrammaticToplinesReady(true);
      }
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    advertiserFlightRange,
    livePlatforms,
    programmaticAggregateScopeKey,
    governedAdvertiserId,
    timeframe,
  ]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      if (!governedAdvertiserId || !API_BASE_URL) {
        if (!cancelled) {
          setStackAdaptCampaigns([]);
          setStackAdaptCampaignsReady(true);
        }
        return;
      }

      const token = getToken();
      if (!token) {
        if (!cancelled) {
          setStackAdaptCampaigns([]);
          setStackAdaptCampaignsReady(true);
        }
        return;
      }

      if (!cancelled) {
        setStackAdaptCampaignsReady(false);
      }

      try {
        const res = await fetch(
          `${safeBase(API_BASE_URL)}/api/stackadapt/${encodeURIComponent(
            governedAdvertiserId
          )}/campaigns?ts=${Date.now()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
            credentials: "include",
            signal: controller.signal,
          }
        );
        const payload = await res.json().catch(() => null);
        if (cancelled) return;

        const rows = Array.isArray(payload?.rows)
          ? payload.rows
          : Array.isArray(payload?.normalizedRows)
          ? payload.normalizedRows
          : [];
        setStackAdaptCampaigns(res.ok ? rows : []);
        setStackAdaptCampaignsReady(true);
      } catch (error) {
        if (cancelled || controller.signal.aborted) return;
        console.error("[ProgrammaticAnalytics][StackAdapt] campaigns fetch failed", error);
        setStackAdaptCampaigns([]);
        setStackAdaptCampaignsReady(true);
      }
    };

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [governedAdvertiserId]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      if (!governedAdvertiserId || !API_BASE_URL) {
        if (!cancelled) {
          setStackAdaptHealth(null);
          setStackAdaptHealthReady(true);
          setStackAdaptHealthError("");
        }
        return;
      }

      const token = getToken();
      const fallbackState = buildClientProgrammaticUnavailableState({
        advertiserId: governedAdvertiserId,
        rawAdvertiser: currentAdvertiserRaw,
      });
      if (!token) {
        if (!cancelled) {
          setStackAdaptHealth(fallbackState);
          setStackAdaptHealthReady(true);
          setStackAdaptHealthError("");
        }
        return;
      }

      if (!cancelled) {
        setStackAdaptHealthReady(false);
        setStackAdaptHealthError("");
      }

      try {
        const res = await fetch(
          `${safeBase(API_BASE_URL)}/api/stackadapt/${encodeURIComponent(
            governedAdvertiserId
          )}/status?ts=${Date.now()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
            credentials: "include",
            signal: controller.signal,
          }
        );

        const payload = await res.json().catch(() => null);
        if (cancelled) return;

        const mergedPayload = mergeStackAdaptHealthWithFallback(
          payload,
          fallbackState
        );

        if (!res.ok) {
          if (mergedPayload) {
            setStackAdaptHealth(mergedPayload);
            setStackAdaptHealthError("");
          } else if (fallbackState) {
            setStackAdaptHealth(fallbackState);
            setStackAdaptHealthError("");
          } else {
            setStackAdaptHealth(null);
            setStackAdaptHealthError(
              normalizeClientProgrammaticStatusMessage(
                payload?.message || payload?.error
              )
            );
          }
          setStackAdaptHealthReady(true);
          return;
        }

        if (mergedPayload) {
          setStackAdaptHealth(mergedPayload);
          setStackAdaptHealthError("");
        } else if (fallbackState) {
          setStackAdaptHealth(fallbackState);
          setStackAdaptHealthError("");
        } else {
          setStackAdaptHealth(null);
          setStackAdaptHealthError(CLIENT_PROGRAMMATIC_STATUS_FALLBACK);
        }
        setStackAdaptHealthReady(true);
      } catch (error) {
        if (cancelled || controller.signal.aborted) return;
        console.error("[ProgrammaticAnalytics][StackAdapt] status fetch failed", error);
        if (fallbackState) {
          setStackAdaptHealth(fallbackState);
          setStackAdaptHealthError("");
        } else {
          setStackAdaptHealth(null);
          setStackAdaptHealthError(CLIENT_PROGRAMMATIC_STATUS_FALLBACK);
        }
        setStackAdaptHealthReady(true);
      }
    };

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [governedAdvertiserId, currentAdvertiserRaw]);

  const stackAdaptReportSurface = useMemo(
    () =>
      stackAdaptHealth?.reportSurface ||
      stackAdaptHealth?.integrationNotes?.reportSurface ||
      null,
    [stackAdaptHealth]
  );

  const resolvedStackAdaptReportSurface = useMemo(
    () =>
      resolvePrioritizedProgrammaticReportSurface(
        stackAdaptReportSurface,
        currentAdvertiserRaw
      ),
    [currentAdvertiserRaw, stackAdaptReportSurface]
  );

  const stackAdaptConnectedChannelLabels = useMemo(() => {
    return getPrioritizedProgrammaticConnectedChannels(
      stackAdaptReportSurface,
      buildAdvertiserProgrammaticFallback(currentAdvertiserRaw).reportSurface
    ).map((key) => getProgrammaticChannelDisplayLabel(key));
  }, [currentAdvertiserRaw, stackAdaptReportSurface]);

  // The Omni report action was retired from this panel, so every variant that
  // described a report link the panel no longer offers is gone. What remains
  // is the one governed provider-status disclosure, and it now speaks only
  // when provider status is genuinely unavailable.
  const stackAdaptProviderStatusNote = useMemo(() => {
    if (stackAdaptHealth?.liveReadState === "status_unavailable") {
      return "Provider status is currently unavailable for this advertiser.";
    }
    // The same disclosure slot now also speaks when the canonical reporting
    // says part of a channel is unavailable — in provider-neutral words.
    return describePartialChannelReporting(
      listPartiallyReportingChannels(estateModel?.channelReporting, OMNI_CHANNEL_DEFINITIONS)
    );
  }, [stackAdaptHealth, estateModel]);

  const contributionMetric = useMemo(
    () => resolveOmniPerformanceMetric(contributionMetricKey),
    [contributionMetricKey]
  );

  /*
   * ONE derivation for the whole panel. The KPI tiles, the contribution bars,
   * the platform counts and the leading surface are all read off this model,
   * so a tile is the column sum of the bars beneath it by construction. It is
   * built from the canonical estate rows the surrounding campaign context
   * already resolved for the approved window — the same rows Platform
   * Analytics draws — and never from the per-channel programmatic topline
   * sweep, which prices spend through the programmatic rate card and cannot
   * see the social estate at all.
   */
  const omniPerformance = useMemo(
    () => buildOmniPerformanceModel(estateModel?.platforms),
    [estateModel]
  );

  const contributionBasis = omniPerformance.byMetric[contributionMetric.key];
  const contributionRows = contributionBasis?.rows || [];

  const topPerformingPlatform = useMemo(() => {
    const platformLabel = normalizeProgrammaticChannelLabel(
      omniPerformance.leadPlatform?.label || ""
    );

    if (!platformLabel) return null;

    return {
      label: platformLabel,
      image: resolveProgrammaticChannelImage(platformLabel),
      meta: "Leading canonical omni surface for this reporting window.",
    };
  }, [omniPerformance.leadPlatform]);

  /*
   * The status card reads the SAME canonical estate the tiles and bars read.
   * It used to rank off the programmatic topline sweep's source tier, so the
   * panel could name a leading surface that appeared nowhere in the bars.
   */
  const omniPlatformCardState = useMemo(() => {
    const toplineEntries = Object.values(programmaticToplines || {}).filter(Boolean);
    const hasAnyMeaningfulLiveData = toplineEntries.some((entry) => hasMeaningfulToplineData(entry));
    const pacingStatuses = toplineEntries
      .map((entry) => String(entry?.raw?.pacingStatus || entry?.pacingStatus || "").trim().toLowerCase())
      .filter(Boolean);
    const isPreFlight = pacingStatuses.includes("pre-flight");
    const configuredPlatformLabel =
      normalizeProgrammaticChannelLabel(resolvedStackAdaptReportSurface.primaryChannelLabel) ||
      normalizeProgrammaticChannelLabel(stackAdaptConnectedChannelLabels[0] || "");

    if (omniPerformance.hasEvidence) {
      return {
        label: "Campaign Status",
        value: "Live",
        meta: topPerformingPlatform?.label
          ? `Approved-window delivery is active. Leading omni surface by spend: ${topPerformingPlatform.label}.`
          : "Approved-window delivery is active across the current omni mix.",
        image: null,
      };
    }

    if (isPreFlight) {
      return {
        label: "Campaign Status",
        value: "Pre-Live",
        meta: "Omni platform ranking will appear after the flight begins and live delivery data is available.",
        image: null,
      };
    }

    if (!hasAnyMeaningfulLiveData && configuredPlatformLabel) {
      return {
        label: "Primary Connected Platform",
        value: configuredPlatformLabel,
        meta: resolvedStackAdaptReportSurface.primaryChannelLabel
          ? "Primary mapped programmatic report surface. No live comparative platform data is available yet."
          : "Configured omni channel in scope. Live ranking will appear once delivery data is available.",
        image: resolveProgrammaticChannelImage(configuredPlatformLabel),
      };
    }

    return {
      label: "Campaign Status",
      value: "Awaiting Delivery Data",
      meta: "No live comparative omni platform data is available for the current window.",
      image: null,
    };
  }, [
    omniPerformance.hasEvidence,
    programmaticToplines,
    resolvedStackAdaptReportSurface.primaryChannelLabel,
    stackAdaptConnectedChannelLabels,
    topPerformingPlatform,
  ]);

  const canonicalMetricCards = useMemo(
    () => buildOmniPerformanceMetricCards(omniPerformance),
    [omniPerformance]
  );

  const prioritizedMetricCards = useMemo(() => {
    const prioritized = [...canonicalMetricCards];

    if (omniPlatformCardState) {
      prioritized.push({
        id: `omni-platform-state-${String(omniPlatformCardState.value || "unknown")
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")}`,
        label: omniPlatformCardState.label,
        value: omniPlatformCardState.value,
        meta: omniPlatformCardState.meta,
        image: omniPlatformCardState.image,
        type: "top-platform",
      });
    }

    return prioritized;
  }, [canonicalMetricCards, omniPlatformCardState]);

  const programmaticMetricsLoading = useMemo(
    () =>
      resolveProgrammaticMetricLoadingState({
        currentAdvertiserRaw,
        canonicalEstateReady: estateModelReady,
        prioritizedMetricCards,
      }),
    [currentAdvertiserRaw, estateModelReady, prioritizedMetricCards]
  );

  const showProgrammaticMetricArea = useMemo(
    () => programmaticMetricsLoading || prioritizedMetricCards.length > 0,
    [prioritizedMetricCards.length, programmaticMetricsLoading]
  );

  const omniIntelligenceModel = useMemo(
    () =>
      buildOmniPlatformIntelligenceModel({
        definitions: OMNI_ANALYTICS_TILE_DEFINITIONS,
        socialEntries: socialPlatformEntries,
        omniEntries: omniChannelEntries,
        toplinesByPlatform: programmaticToplines,
        sharedAudienceResolution,
        advertiserSelected: Boolean(currentAdvertiserRaw),
        role: "client",
      }),
    [
      currentAdvertiserRaw,
      omniChannelEntries,
      programmaticToplines,
      sharedAudienceResolution,
      socialPlatformEntries,
    ]
  );

  // Estate readiness counts come from the same evidence-based intelligence
  // model that drives the Omni Intelligence section — never from tile counts.
  const estateReadiness = useMemo(() => {
    const summary = omniIntelligenceModel?.summary || {};
    const represented = Number(summary.represented) || 0;
    const connected = Number(summary.connected) || 0;
    const reporting = Number(summary.reporting) || 0;
    const stale = Number(summary.stale) || 0;
    return {
      represented,
      reporting,
      awaiting: Math.max(connected - reporting, 0),
      unavailable: Math.max(represented - connected, 0),
      stale,
    };
  }, [omniIntelligenceModel]);

  const estatePillLabel = useMemo(() => {
    if (!governedAdvertiserId) return "Awaiting Advertiser";
    if (omniReadinessLoading) return "Checking";
    if (estateReadiness.reporting > 0) return "Reporting Active";
    if (estateReadiness.awaiting > 0) return "Connected";
    return "Connection Pending";
  }, [
    estateReadiness.awaiting,
    estateReadiness.reporting,
    governedAdvertiserId,
    omniReadinessLoading,
  ]);

  const estateFreshnessNote = useMemo(() => {
    const staleLabels = (omniIntelligenceModel?.platforms || [])
      .filter((platform) => platform?.freshness?.state === "stale")
      .map((platform) => platform.displayLabel || platform.label)
      .filter(Boolean);
    if (staleLabels.length) {
      const suffix =
        staleLabels.length > 1 ? ` and ${staleLabels.length - 1} more` : "";
      return `Reporting freshness limited by ${staleLabels[0]}${suffix}.`;
    }
    if (estateModel?.freshnessAt) {
      const relative = formatFreshness(estateModel.freshnessAt);
      return relative ? `Canonical reporting refreshed ${relative}.` : "";
    }
    return "";
  }, [estateModel?.freshnessAt, omniIntelligenceModel]);

  const advertiserSelectionResolved = useMemo(() => {
    if (!catalogReady || !accessReady) return false;
    if (governedAdvertiserId) return true;
    return visibleAdvertisers.length !== 1;
  }, [
    accessReady,
    catalogReady,
    governedAdvertiserId,
    visibleAdvertisers.length,
  ]);

  const initialShellReady = useMemo(() => {
    if (!catalogReady || !accessReady) return false;
    if (!advertiserSelectionResolved) return false;
    return true;
  }, [accessReady, advertiserSelectionResolved, catalogReady]);

  const initialDisplayReady = useMemo(() => {
    if (!initialShellReady) return false;

    if (!currentAdvertiserRaw) {
      return true;
    }

    /*
     * First paint waits on the source of the figures the page states — the
     * canonical estate — not on the per-channel pacing sweep, which no longer
     * feeds anything visible above the fold.
     */
    return estateModelReady;
  }, [currentAdvertiserRaw, estateModelReady, initialShellReady]);

  const shouldDismissInitialLoader = useMemo(() => {
    if (initialDisplayReady) return true;
    if (!loaderSafetyElapsed) return false;
    return initialShellReady;
  }, [initialDisplayReady, initialShellReady, loaderSafetyElapsed]);

  useEffect(() => {
    if (initialLoaderDismissedRef.current) return undefined;

    const timer = window.setTimeout(() => {
      setLoaderSafetyElapsed(true);
    }, INITIAL_LOADER_MAX_HOLD_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (initialLoaderDismissedRef.current) return undefined;
    if (!shouldDismissInitialLoader) {
      setShowInitialLoader(true);
      return undefined;
    }

    let raf1 = 0;
    let raf2 = 0;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        initialLoaderDismissedRef.current = true;
        setShowInitialLoader(false);
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [shouldDismissInitialLoader]);

  const programmaticPageAwareness = useMemo(
    () => ({
      routeKey: "/programmatic-analytics",
      pageTitle: PROGRAMMATIC_PAGE_TITLE,
      pageType: "analytics",
      advertiserId: governedAdvertiserId,
      advertiserName: governedAdvertiserName,
      timeframe,
      platformKey: "programmatic_rollup",
      platformLabel: "Programmatic",
      toplineScope: "aggregate",
      pageCapabilities: [
        "programmatic performance overview",
        "cross-platform pacing read",
        "live platform aggregate read",
        "inventory mix context",
      ],
      /*
       * Awareness quotes what the page states. Since the panel now states the
       * canonical estate, so does this — an assistant reading the old
       * programmatic-only aggregate would have answered with figures no
       * longer visible anywhere on the surface.
       */
      aggregateToplineMetrics: canonicalMetricCards,
      metadata: {
        eligiblePlatformCount: omniPerformance.contributingPlatformCount,
        includedPlatformKeys: omniPerformance.rows.map((row) => row.key),
        includedPlatformLabels: omniPerformance.rows.map((row) => row.label),
        topPlatform: omniPerformance.leadPlatform?.label || "",
        stackAdaptChannels: STACKADAPT_PROGRAMMATIC_CHANNELS,
        stackAdaptProvider: CLIENT_PROGRAMMATIC_PROVIDER_LABEL,
        stackAdaptConnectedChannels: stackAdaptConnectedChannelLabels,
      },
    }),
    [
      canonicalMetricCards,
      governedAdvertiserId,
      governedAdvertiserName,
      omniPerformance,
      stackAdaptConnectedChannelLabels,
      timeframe,
    ]
  );

  usePageAwareness(programmaticPageAwareness);

  return (
    <div className="core-dashboard omni-analytics-page-container">
      <Loading active={showInitialLoader} />
      <div className="core-dashboard-content omni-analytics-page-content cortex-page-intro-shell">
        <CphereHeaderLogo
          alt="Powered by Logo"
          className="omni-analytics-poweredby-logo"
          onClick={() => navigate("/client")}
        />

        <div className="omni-analytics-hero-stack cphere-governed-hero-selector-stack">
          <div className="omni-analytics-hero-container">
            <div className="omni-analytics-hero-pill">
              <h1 className="omni-analytics-hero-pill-label cphere-hero-pill-wordmark" aria-label="Omni Analytics">Omni</h1>
              <span className="omni-analytics-hero-pill-tag cphere-hero-pill-tag">Analytics</span>
            </div>
            <div className="omni-analytics-hero-shimmer-bar" />
          </div>

          <div className="omni-analytics-advertiser-selector-stage cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiserRecord}
              advertiserName={currentAdvertiserRecord?.name || ""}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={handleAdvertiserSelectorOpen}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={currentAdvertiserRecord?.name || ""}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        <section
          className="omni-analytics-tile-group omni-analytics-tile-group--social"
          aria-labelledby="omni-social-platforms"
        >
          <h2 id="omni-social-platforms" className="omni-analytics-group-heading">
            Social Platforms
          </h2>
          <div className="omni-analytics-tile-grid">
            {socialPlatformEntries.map((platform) => (
              <button
                key={platform.key}
                className={platform.imageFit ? "omni-analytics-tile omni-analytics-tile--fitted" : "omni-analytics-tile"}
                onClick={() => navigate(platform.route)}
                style={{ backgroundImage: `url(${platform.image})`, ...(platform.imageFit ? { backgroundSize: platform.imageFit } : {}) }}
                type="button"
              >
                {platform.connected && (
                  <span className="omni-analytics-connected-badge">
                    {platform.statusBadgeLabel || "CONNECTED"}
                  </span>
                )}
                <span className="omni-analytics-tile-label">
                  {platform.tileLabel || platform.label}
                </span>
              </button>
            ))}
          </div>
        </section>
        <section className="omni-analytics-tile-group" aria-labelledby="omni-channels">
          <h2 id="omni-channels" className="omni-analytics-group-heading">
            Omni Channels
          </h2>
          <div className="omni-analytics-tile-grid">
            {omniChannelEntries.map((channel) => (
              <button
                key={channel.key}
                className="omni-analytics-tile"
                onClick={() => navigate(channel.route)}
                style={{ backgroundImage: `url(${channel.image})` }}
                type="button"
              >
                {channel.connected && (
                  <span className="omni-analytics-connected-badge">
                    {channel.statusBadgeLabel || "CONNECTED"}
                  </span>
                )}
                <span className="omni-analytics-tile-label">{channel.label}</span>
              </button>
            ))}
          </div>
        </section>

        <OmniIntelligencePanel
          model={omniIntelligenceModel}
          onNavigate={navigate}
          loading={omniReadinessLoading}
          error={omniReadinessError}
        />

        <div className="omni-analytics-provider-panel">
          <div className="omni-analytics-provider-panel-copy">
            <div className="omni-analytics-provider-header">
              <div className="omni-analytics-provider-header-stack">
                <span className="omni-analytics-provider-logo-ring">
                  <img
                    src={campaignManagementImage}
                    alt="Cphere"
                    className="omni-analytics-provider-logo omni-analytics-provider-logo-campaignmanagement"
                  />
                </span>
                <div className="omni-analytics-provider-text">
                  <div className="omni-analytics-provider-title-stack">
                    <div className="omni-analytics-provider-title">
                      {OMNI_PERFORMANCE_PANEL_TITLE}
                    </div>
                    <div className="omni-analytics-provider-subtitle">
                      {OMNI_PERFORMANCE_PANEL_SUBTITLE}
                    </div>
                    <div
                      className={`omni-analytics-provider-connected-pill ${
                        estateReadiness.reporting > 0
                          ? "omni-analytics-provider-connected-pill-ready"
                          : "omni-analytics-provider-connected-pill-pending"
                      }`}
                    >
                      {estatePillLabel}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="omni-analytics-performance-readiness"
              aria-label="Governed reporting status across the platform estate"
            >
              <span className="omni-analytics-performance-readiness-item">
                {estateReadiness.reporting} destinations reporting
              </span>
              <span className="omni-analytics-performance-readiness-item">
                {estateReadiness.awaiting} awaiting data
              </span>
              <span className="omni-analytics-performance-readiness-item">
                {estateReadiness.unavailable} unavailable
              </span>
              {estateReadiness.stale > 0 ? (
                <span className="omni-analytics-performance-readiness-item omni-analytics-performance-readiness-item--stale">
                  {estateReadiness.stale} stale
                </span>
              ) : null}
            </div>
            {estateCoverage?.text ? (
              <div
                className="omni-analytics-performance-coverage"
                aria-label="Campaign coverage for this reporting window"
              >
                {estateCoverage.text}
              </div>
            ) : null}
            {estateFreshnessNote ? (
              <div className="omni-analytics-performance-freshness">
                {estateFreshnessNote}
              </div>
            ) : null}

            {estateModelReady && estateModel ? (
              <div className="omni-analytics-performance-freshness">
                <div data-reporting-window="effective">Reporting window: {estateModel.reportingWindow?.label || "Not available"}</div>
                {timeframe?.start && timeframe?.end ? (
                  <div data-reporting-window="requested">Requested window: {timeframe.start} → {timeframe.end}</div>
                ) : null}
              </div>
            ) : null}

            {showProgrammaticMetricArea ? (
              <div
                className={`omni-analytics-provider-metric-shell ${
                  programmaticMetricsLoading
                    ? "omni-analytics-provider-metric-shell-loading"
                    : "omni-analytics-provider-metric-shell-ready"
                }`}
                aria-busy={programmaticMetricsLoading}
              >
                {programmaticMetricsLoading ? (
                  <div className="omni-analytics-provider-metric-loading-copy">
                    Loading canonical omni reporting...
                  </div>
                ) : null}
                {!programmaticMetricsLoading && !estateModelReady ? (
                  <div className="omni-analytics-provider-metric-loading-copy">
                    Refreshing canonical omni reporting...
                  </div>
                ) : null}
                {!programmaticMetricsLoading && estateModelError ? (
                  <div className="omni-analytics-provider-metric-loading-copy">
                    {estateModelError}
                  </div>
                ) : null}

                <div
                  className={`omni-analytics-provider-metric-grid ${
                    programmaticMetricsLoading
                      ? "omni-analytics-provider-metric-grid-loading"
                      : "omni-analytics-provider-metric-grid-ready"
                  }`}
                >
                  {programmaticMetricsLoading
                    ? renderProgrammaticMetricSkeletonCards()
                    : prioritizedMetricCards.map((card) => (
                        <div
                          key={card.id}
                          className="omni-analytics-provider-metric-card omni-analytics-provider-metric-card-ready"
                        >
                          <div className="omni-analytics-provider-metric-label">{card.label}</div>
                          {card.type === "top-platform" ? (
                            <div className="omni-analytics-provider-platform-value">
                              {card.image ? (
                                <span className="omni-analytics-provider-platform-logo-shell" aria-hidden="true">
                                  <img
                                    src={card.image}
                                    alt=""
                                    className="omni-analytics-provider-platform-logo"
                                    draggable={false}
                                  />
                                </span>
                              ) : null}
                              <span className="omni-analytics-provider-platform-name">{card.value}</span>
                            </div>
                          ) : (
                            <div className="omni-analytics-provider-metric-value">{card.value}</div>
                          )}
                          <div className="omni-analytics-provider-metric-meta">{card.meta}</div>
                        </div>
                      ))}
                </div>
              </div>
            ) : null}

            <div className="omni-analytics-performance-contribution">
              <div className="omni-analytics-performance-contribution-header">
                <h3 className="omni-analytics-performance-contribution-title">
                  Platform Contribution
                </h3>
                <div
                  className="omni-analytics-performance-metric-toggle"
                  role="group"
                  aria-label="Contribution metric"
                >
                  {OMNI_PERFORMANCE_METRICS.map((metric) => (
                    <button
                      key={metric.key}
                      type="button"
                      aria-pressed={metric.key === contributionMetric.key}
                      className={`omni-analytics-performance-metric-chip ${
                        metric.key === contributionMetric.key
                          ? "omni-analytics-performance-metric-chip--active"
                          : ""
                      }`}
                      onClick={() => setContributionMetricKey(metric.key)}
                    >
                      {metric.label}
                    </button>
                  ))}
                </div>
              </div>
              {contributionRows.length ? (
                <>
                  {/*
                   * The reconciliation line: these bars sum to this figure,
                   * and this figure is the tile above them.
                   */}
                  <p className="omni-analytics-performance-contribution-total">
                    {describeOmniContributionTotal(contributionBasis)}
                  </p>
                  <ul className="omni-analytics-performance-bars">
                    {contributionRows.map((row) => (
                      <li
                        key={row.key}
                        className="omni-analytics-performance-bar-row"
                      >
                        <span className="omni-analytics-performance-bar-name">
                          {row.label}
                        </span>
                        <span className="omni-analytics-performance-bar-track">
                          <span
                            className="omni-analytics-performance-bar-fill"
                            style={{
                              width: `${row.widthPercent}%`,
                              background:
                                resolvePlatformBranding(row.key)?.color ||
                                "var(--cphere-cyan, #7ee7ff)",
                            }}
                          />
                        </span>
                        <span className="omni-analytics-performance-bar-value">
                          <span className="omni-analytics-performance-bar-amount">
                            {formatOmniPerformanceValue(
                              row.value,
                              contributionMetric.kind
                            )}
                          </span>
                          <span className="omni-analytics-performance-bar-share">
                            {row.share.toFixed(row.share < 10 ? 1 : 0)}%
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="omni-analytics-performance-empty">
                  {estateModelError ||
                    (estateModelReady
                      ? "Platform contribution appears once governed platform delivery is observed for this reporting window."
                      : "Resolving governed estate performance…")}
                </p>
              )}
              <p className="omni-analytics-performance-daily-note">
                {OMNI_PERFORMANCE_DAILY_SERIES_NOTE}
              </p>
            </div>

            {stackAdaptProviderStatusNote ? (
              <div className="omni-analytics-provider-note">
                {stackAdaptProviderStatusNote}
              </div>
            ) : null}

            <div className="omni-analytics-provider-actions">
              <button
                type="button"
                className="omni-analytics-provider-button"
                onClick={() => navigate(OMNI_PERFORMANCE_PRIMARY_ACTION_ROUTE)}
              >
                {OMNI_PERFORMANCE_PRIMARY_ACTION_LABEL}
              </button>
            </div>
          </div>
        </div>

      </div>

      <div className="one-back-button" onClick={() => navigate(-1)}>
        <img src={reverseArrowImage} alt="Back" />
      </div>
    </div>
  );
};

export default ProgrammaticAnalytics;
